// 先代の姫の指折りを「部屋の灯り」で知らせる3案（2026-11-05・駅0⁗）。
//   作者（11-05 tell=light）「例のような露骨なものではなく、エクスペディション３３のランプマスター位には自然にしてほしい／個々のデザイン方法について」
//   手本＝ランプマスターの灯り：灯りがボスの体と部屋の物で、灯っている数がそのまま次の技の強さ。数字や印は出さない。
//   3案とも体の動きは princess_count_tells.ts の C（左手は腰・2回突いて合図・大きく踏み込む）と同じ。違うのは部屋の灯りだけ。
//   A 脚光＝舞台の縁の床に5つ。突きの風で彼女の側から1つずつ消える（消えた数＝折った数）。合図で残りがすっと沈み、技で明るく戻る。
//   B シャンデリア＝天井から吊った5本の蝋燭。突くたびに剣先から声（一粒の灯）が飛んで1本灯す（灯った数＝折った数）。合図で炎が主人公へなびく。
//   C 追う照明＝天井から彼女だけを照らす光の輪。突くたびに輪が1段狭まる。合図で輪が主人公へ移って白く固まり、そこへ大技（装置の「追う照明」と兼ねる）。
//   11-05 作者「ここのデザイン方法について考えたいから１０案くらい出してほしい。他ゲームの例も見たうえで考えて」で10案に広げ、動画は3つ（C と下の D・E）。
//   D 床に残る光の跡＝突くたびに剣先の光が床に1本残る（レディ・マリアの血の跡が燃える形）。合図で跡が灯り直してつながり、大技の通り道になる。跡の数＝届く遠さ。
//   E 照明の色＝舞台全体を照らす色が、突くたびに次の技の色へ1段寄る（ジンオウガの帯電の形）。合図で色が決まり、技の後に元へ戻る。
//   拍は前の版と同じ仮の値。灯りの数・色・速さは技を組む時に決め直す。

import { circ, poly } from '../body'
import { trackAt, type MockupMove, type StageEnv, type Tracks, type Vec } from '../core'
import { BEAT_AC, armTracks, bodyTracks, build, jabPhases, lungePhases, mockup } from './princess_count_tells'

const B = BEAT_AC
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const ramp = (t: number, a: number, b: number) => clamp01((t - a) / (b - a))
const smooth = (u: number) => u * u * (3 - 2 * u)
type Ctx = CanvasRenderingContext2D

/** 灯りの暈（ぼかしの代わりに薄い円を重ねる） */
function glow(ctx: Ctx, q: Vec, r: number, col: string, a: number): void {
  if (a <= 0.01) return
  const keep = ctx.globalAlpha
  for (const [rr, aa] of [[1, 0.10], [0.62, 0.16], [0.34, 0.34]] as const) { ctx.globalAlpha = keep * a * aa; circ(ctx, q, r * rr, col) }
  ctx.globalAlpha = keep
}
/** 消えた灯りから上がる細い煙（消えてから 700ms） */
function smoke(ctx: Ctx, q: Vec, since: number, k: number): void {
  if (since < 0 || since > 700) return
  const keep = ctx.globalAlpha, u = since / 700
  for (let i = 0; i < 4; i++) {
    const v = clamp01(u * 1.3 - i * 0.12)
    ctx.globalAlpha = keep * 0.35 * (1 - u)
    circ(ctx, [q[0] + Math.sin((v + i) * 5) * 2 * k, q[1] - (4 + v * 26 + i * 3) * k], (1.6 + v * 2) * k, '#8a93a3')
  }
  ctx.globalAlpha = keep
}

const body = (): Tracks => ({ ...bodyTracks(B), ...armTracks([[0, 'hip'], [B.lunge - 200, 'hip'], [B.lunge + 80, 'back'], [B.lunge + 400, 'back'], [B.lunge + 800, 'hip']]) })
/** 剣先の位置（模型の空間・rx を足した物） */
function tipWorld(tr: Tracks, t: number): Vec {
  const v: Record<string, number | Vec> = {}
  for (const k in tr) v[k] = trackAt(tr[k]!, t)
  const p = build(v), h = p.hF as Vec, a = (p.sF as number) * Math.PI / 180
  return [h[0] + Math.cos(a) * 64 + (p.rx as number), h[1] + Math.sin(a) * 64]
}

/** 合図の間（tell→lunge）に沈み、技の当たりで明るく戻る倍率 */
export const tellDip = (t: number) => t < B.lunge - 40 ? 1 - 0.35 * smooth(ramp(t, B.tell, B.tell + 220)) : 1 + 0.7 * (1 - smooth(ramp(t, B.lunge - 40, B.lunge + 320)))

// ---------- A 脚光 ----------
/** 舞台の縁の5つ（幅 400 の画面の x）。消える順＝彼女の側（右）から */
const FOOT_X = [64, 132, 200, 268, 336]
/** 消える時刻と灯し直す時刻（index 4 → 3 の順に消え、3 → 4 の順に灯し直す） */
export const PRINCESS_LIGHT_FOOT_BEAT = { ...B, out: { 4: B.j1 + 40, 3: B.j2 + 40 } as Record<number, number>, relight: { 3: B.lunge + 620, 4: B.lunge + 760 } as Record<number, number> }
const FA = PRINCESS_LIGHT_FOOT_BEAT
/** i 番目の脚光の明るさ 0〜1（合図の沈みは含めない） */
export function footLamp(i: number, t: number): number {
  const o = FA.out[i]
  if (o == null) return 1
  // 突きの風で2度揺れてから消える
  const flick = t >= o - 40 && t < o + 40 ? 0.55 + 0.45 * Math.abs(Math.cos((t - o) / 14)) : 1
  const out = t < o + 40 ? flick : 1 - smooth(ramp(t, o + 40, o + 200))
  return Math.max(out, smooth(ramp(t, FA.relight[i]!, FA.relight[i]! + 180)))
}
function drawFoot(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, y = G + 20 * k, dip = tellDip(t)
  FOOT_X.forEach((x0, i) => {
    const x = x0 * k, v = footLamp(i, t), m = v * dip
    ctx.globalAlpha = 0.14 * clamp01(m); ctx.fillStyle = '#ffcf7a'
    ctx.beginPath(); ctx.ellipse(x, G + 1 * k, 30 * k * Math.min(1.3, m), 4 * k, 0, 0, Math.PI * 2); ctx.fill()
    ctx.globalAlpha = 1
    glow(ctx, [x, y - 3 * k], 22 * k * Math.min(1.4, 0.6 + 0.4 * m), '#ffcf7a', clamp01(m))
    if (m > 0.05) { ctx.globalAlpha = clamp01(m); circ(ctx, [x, y - 3 * k], 2.4 * k, '#fff1c9'); ctx.globalAlpha = 1 }
    poly(ctx, [[x - 8 * k, y - 1 * k], [x + 8 * k, y - 1 * k], [x + 6 * k, y + 5 * k], [x - 6 * k, y + 5 * k]], '#2a303b')
    const o = FA.out[i]
    if (o != null) smoke(ctx, [x, y - 4 * k], t - (o + 160), k)
  })
}
export const PRINCESS_LIGHT_FOOT_MOCKUP: MockupMove = {
  ...mockup('princess-light-foot', '灯り A 脚光（舞台の縁の5つの灯りが、突きの風で先代の側から1つずつ消える）', B, body(),
    [...jabPhases(B, '1回目の突き・突きの風で脚光が1つ消える', '2回目の突き・もう1つ消える'),
      ...lungePhases(B, '合図：残った脚光がすっと沈む（息を吸うように）')], [FA.out[4]!, FA.out[3]!],
    '拍は前の版と同じ仮の値。帯の黄色＝脚光が消えた瞬間。戻りで消えた脚光が灯し直される（次の数え直し）。数値は技を組む時に決め直す。'),
  stage: { front: drawFoot },
}

// ---------- B シャンデリア ----------
const CH = { x: 228, y: 64, dx: [-52, -26, 0, 26, 52] }
const bodyB = body()
/** 声が剣先を離れる時刻・蝋燭に着く時刻（index 4 → 3 の順に灯る）・まとめて消える時刻 */
export const PRINCESS_LIGHT_CHANDELIER_BEAT = { ...B, born: { 4: B.j1, 3: B.j2 } as Record<number, number>, fly: 200, out: B.lunge + 600 }
const CB = PRINCESS_LIGHT_CHANDELIER_BEAT
/** i 番目の蝋燭の炎 0〜1 */
export function candle(i: number, t: number): number {
  const b = CB.born[i]
  if (b == null) return 0
  return smooth(ramp(t, b + CB.fly, b + CB.fly + 80)) * (1 - smooth(ramp(t, CB.out, CB.out + 160)))
}
function drawChandelier(ctx: Ctx, t: number, e: StageEnv): void {
  const { k } = e
  // 合図の間だけ、吊った所を軸に小さく揺れる
  const sw = t > B.tell && t < B.lunge + 500 ? Math.sin((t - B.tell) / 130) * 0.05 * (1 - ramp(t, B.lunge, B.lunge + 500)) : 0
  const rot = (x: number, y: number): Vec => { const X = x - CH.x, c = Math.cos(sw), s = Math.sin(sw); return [(CH.x + X * c - y * s) * k, (X * s + y * c) * k] }
  const hold = CH.dx.map(dx => rot(CH.x + dx, CH.y + dx * dx / 450))
  ctx.strokeStyle = '#4a5262'; ctx.lineWidth = 1.5 * k; ctx.lineCap = 'round'
  const top = rot(CH.x, 0), hub = rot(CH.x, CH.y - 12)
  ctx.beginPath(); ctx.moveTo(top[0], top[1]); ctx.lineTo(hub[0], hub[1]); ctx.stroke()
  ctx.lineWidth = 2.2 * k; ctx.beginPath(); hold.forEach((q, i) => i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1])); ctx.stroke()
  for (const q of [hold[0]!, hold[4]!]) { ctx.lineWidth = 1 * k; ctx.beginPath(); ctx.moveTo(hub[0], hub[1]); ctx.lineTo(q[0], q[1]); ctx.stroke() }
  // 合図の間、炎が主人公の側（左）へなびいて伸びる
  const lean = t > B.tell && t < B.lunge ? smooth(ramp(t, B.tell, B.tell + 200)) : 0
  hold.forEach((q, i) => {
    poly(ctx, [[q[0] - 1.6 * k, q[1]], [q[0] + 1.6 * k, q[1]], [q[0] + 1.6 * k, q[1] - 7 * k], [q[0] - 1.6 * k, q[1] - 7 * k]], '#8f97a6')
    const v = candle(i, t)
    if (v <= 0.01) return
    const fl = 1 + 0.08 * Math.sin(t / 37 + i * 2), f: Vec = [q[0] - lean * 5 * k, q[1] - (10 + lean * 5) * k * fl]
    glow(ctx, f, 18 * k, '#7fc4ff', v)
    ctx.globalAlpha = v; poly(ctx, [[q[0] - 1.8 * k, q[1] - 7 * k], [q[0] + 1.8 * k, q[1] - 7 * k], f], '#7fc4ff'); circ(ctx, [q[0], q[1] - 8.5 * k], 1.3 * k, '#ffffff'); ctx.globalAlpha = 1
  })
  for (const i of [3, 4]) smoke(ctx, [hold[i]![0], hold[i]![1] - 10 * k], t - (CB.out + 120), k)
  // 剣先から蝋燭へ飛ぶ声（芯の白い粒に細い尾）
  for (const [is, b] of Object.entries(CB.born)) {
    const u = (t - b) / CB.fly
    if (u < 0 || u > 1) continue
    const from = e.at(tipWorld(bodyB, b)), to: Vec = [hold[+is]![0], hold[+is]![1] - 9 * k]
    const P = (w: number): Vec => { const s = 1 - Math.pow(1 - w, 2); return [from[0] + (to[0] - from[0]) * s, from[1] + (to[1] - from[1]) * s - Math.sin(Math.PI * s) * 26 * k] }
    for (let j = 3; j >= 1; j--) { ctx.globalAlpha = 0.25 / j; circ(ctx, P(Math.max(0, u - j * 0.05)), 2 * k, '#7fc4ff') }
    ctx.globalAlpha = 1
    const q = P(u); glow(ctx, q, 14 * k, '#7fc4ff', 1); circ(ctx, q, 3 * k, '#7fc4ff'); circ(ctx, q, 1.5 * k, '#ffffff')
  }
}
export const PRINCESS_LIGHT_CHANDELIER_MOCKUP: MockupMove = {
  ...mockup('princess-light-chandelier', '灯り B シャンデリア（突くたびに剣先から声が飛び、吊ったろうそくを1本ずつ灯す）', B, bodyB,
    [...jabPhases(B, '1回目の突き・声が飛んでろうそくを1本灯す', '2回目の突き・もう1本'),
      ...lungePhases(B, '合図：灯った火が主人公の側へなびき、吊り具が揺れる')], [CB.born[4]! + CB.fly, CB.born[3]! + CB.fly],
    '拍は前の版と同じ仮の値。帯の黄色＝ろうそくが灯った瞬間。戻りでろうそくはまとめて消える（声が戻る）。火の色は声と同じ青。数値は技を組む時に決め直す。'),
  stage: { back: drawChandelier },
}

// ---------- C 追う照明 ----------
/** 光の輪の半幅（幅 400 の画面の px）：5本→62、1本折るごとに 12 狭まる */
const SPOT_W = [62, 50, 38]
export const PRINCESS_LIGHT_SPOT_BEAT = { ...B, narrow: [B.j1 + 120, B.j2 + 120], move: [B.tell, B.tell + 260], back: [B.lunge + 600, B.lunge + 1000] }
const SB = PRINCESS_LIGHT_SPOT_BEAT
const HER_X = 300, HERO_X = 300 - 150 * 0.95
/** 光の輪：中心の x・半幅・白さ（0 暖かい→1 白）・明るさ */
export function spot(t: number): { x: number; w: number; white: number; a: number } {
  let w = SPOT_W[0]!
  SB.narrow.forEach((at, i) => { w += (SPOT_W[i + 1]! - SPOT_W[i]!) * smooth(ramp(t, at, at + 90)) })
  const flick = SB.narrow.some(at => t >= at && t < at + 90) ? 0.7 : 1
  const go = smooth(ramp(t, SB.move[0], SB.move[1])), ret = smooth(ramp(t, SB.back[0], SB.back[1]))
  const x = HER_X + (HERO_X - HER_X) * go * (1 - ret)
  w = w + (30 - w) * go
  w = w + (SPOT_W[0]! - w) * ret
  return { x, w, white: go * (1 - ret), a: flick * (1 + 0.3 * go * (1 - ret)) }
}
function drawSpot(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, sp = spot(t), x = sp.x * k, w = sp.w * k, sx = 352 * k
  for (const [col, a] of [['#ffe6b0', 1 - sp.white], ['#ffffff', sp.white]] as const) {
    if (a <= 0.01) continue
    ctx.globalAlpha = 0.09 * a * sp.a; poly(ctx, [[sx - 5 * k, -4 * k], [sx + 5 * k, -4 * k], [x + w, G], [x - w, G]], col)
    for (const [rr, aa] of [[1, 0.22], [0.62, 0.22]] as const) {
      ctx.globalAlpha = aa * a * sp.a; ctx.fillStyle = col
      ctx.beginPath(); ctx.ellipse(x, G, w * rr, 7 * k * rr, 0, 0, Math.PI * 2); ctx.fill()
    }
  }
  ctx.globalAlpha = 1
}
export const PRINCESS_LIGHT_SPOT_MOCKUP: MockupMove = {
  ...mockup('princess-light-spot', '灯り C 追う照明（先代を照らす光の輪が、突くたびに1段狭まる）', B, body(),
    [...jabPhases(B, '1回目の突き・光の輪が1段狭まる', '2回目の突き・もう1段'),
      ...lungePhases(B, '合図：光の輪が主人公へ移り、白く固まる')], SB.narrow,
    '拍は前の版と同じ仮の値。帯の黄色＝光の輪が狭まった瞬間。戻りで輪は先代へ戻って広がる。数値は技を組む時に決め直す。'),
  stage: { back: drawSpot },
}

// ---------- D 床に残る光の跡 ----------
const bodyD = body()
/** 跡が残る時刻・合図で灯り直してつながる間・消える間 */
export const PRINCESS_LIGHT_TRAIL_BEAT = { ...B, lay: [B.j1 + 60, B.j2 + 60], ignite: [B.tell, B.tell + 240], out: [B.lunge + 300, B.lunge + 900] }
const DB = PRINCESS_LIGHT_TRAIL_BEAT
/** i 本目の跡の明るさ 0〜1（残った直後に強く、すぐ熾火の 0.35 へ。合図で 1 へ灯り直す） */
export function trail(i: number, t: number): number {
  const at = DB.lay[i]
  if (at == null || t < at) return 0
  const ember = 0.35 + 0.65 * (1 - smooth(ramp(t, at + 60, at + 360)))
  const lit = Math.max(ember, smooth(ramp(t, DB.ignite[0], DB.ignite[1])))
  return lit * (1 - smooth(ramp(t, DB.out[0], DB.out[1])))
}
/** 跡と跡をつなぐ線（合図で伸びて大技の通り道になる）の明るさ */
export const trailLink = (t: number) => smooth(ramp(t, DB.ignite[0] + 60, DB.ignite[1])) * (1 - smooth(ramp(t, DB.out[0], DB.out[1])))
function drawTrail(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, x0 = e.at(tipWorld(bodyD, B.j1))[0] / k
  // i 本目の跡は彼女と剣先の間の床に、主人公の側へ 40 ずつずれて残る（同じ所へ突いても重ならない）
  const seg = (i: number): [number, number] => [x0 + 50 - 40 * i, x0 + 84 - 40 * i]
  const reach = HERO_X - 12, flash = 1 + 0.6 * (1 - smooth(ramp(t, B.lunge - 20, B.lunge + 300))) * (t >= B.lunge - 20 ? 1 : 0)
  const line = (a: number, b: number, w: number, col: string, al: number) => {
    if (al <= 0.01) return
    ctx.globalAlpha = al; ctx.strokeStyle = col; ctx.lineWidth = w * k; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(a * k, G - 1 * k); ctx.lineTo(b * k, G - 1 * k); ctx.stroke()
  }
  const ln = trailLink(t)
  if (ln > 0.01) {
    const st = x0 + 84, end = st + (reach - st) * ln
    line(end, st, 7, '#7fc4ff', 0.12 * ln * flash); line(end, st, 2, '#bfe3ff', 0.55 * ln * Math.min(1, flash))
  }
  for (let i = 0; i < DB.lay.length; i++) {
    const v = trail(i, t)
    if (v <= 0.01) continue
    const [a, b] = seg(i), m = v * Math.min(1.6, flash)
    line(a, b, 9, '#7fc4ff', 0.10 * m); line(a, b, 3, '#7fc4ff', 0.6 * v); line(a + 4, b - 4, 1.2, '#ffffff', 0.8 * v)
    glow(ctx, [(a + b) / 2 * k, G - 2 * k], 16 * k, '#7fc4ff', 0.5 * v)
  }
  ctx.globalAlpha = 1
}
export const PRINCESS_LIGHT_TRAIL_MOCKUP: MockupMove = {
  ...mockup('princess-light-trail', '灯り D 床に残る光の跡（突くたびに剣先の光が床に1本残り、合図でつながって大技の通り道になる）', B, bodyD,
    [...jabPhases(B, '1回目の突き・床に光の跡が1本残る', '2回目の突き・もう1本'),
      ...lungePhases(B, '合図：跡が灯り直してつながり、主人公の足元まで伸びる')], DB.lay,
    '拍は前の版と同じ仮の値。帯の黄色＝跡が残った瞬間。跡は残り火のように薄く残り、合図で灯り直す。跡の数で大技の届く遠さが変わる形を想定（影絵は2本の時だけ）。数値は技を組む時に決め直す。'),
  stage: { back: drawTrail },
}

// ---------- E 照明の色 ----------
/** 色が寄る時刻・合図で決まる間・元へ戻る間 */
export const PRINCESS_LIGHT_COLOR_BEAT = { ...B, step: [B.j1 + 80, B.j2 + 80], snap: [B.tell, B.tell + 200], back: [B.lunge + 500, B.lunge + 1000] }
const EB = PRINCESS_LIGHT_COLOR_BEAT
/** 舞台の色の寄り具合 0（暖かい白）〜1（次の技の色）：突くたびに 0.35 ずつ、合図で 1 */
export function hueMix(t: number): number {
  let m = 0
  EB.step.forEach(at => { m += 0.35 * smooth(ramp(t, at, at + 160)) })
  m += (1 - m) * smooth(ramp(t, EB.snap[0], EB.snap[1]))
  return m * (1 - smooth(ramp(t, EB.back[0], EB.back[1])))
}
const WARM = [255, 226, 176], NEXT = [255, 84, 104]
const mixCol = (u: number) => '#' + WARM.map((c, i) => Math.round(c + (NEXT[i]! - c) * u).toString(16).padStart(2, '0')).join('')
function drawColor(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, u = hueMix(t), col = mixCol(u)
  const flash = t >= B.lunge - 20 ? 1 + 0.8 * (1 - smooth(ramp(t, B.lunge - 20, B.lunge + 260))) : 1
  // 寄った直後だけ一瞬ちらつく
  const flick = EB.step.some(at => t >= at && t < at + 70) ? 0.65 : 1
  for (const [sx, fx0, fx1] of [[30, 90, 300], [370, 110, 330]] as const) {
    ctx.globalAlpha = 0.07 * flash * flick; poly(ctx, [[(sx - 8) * k, -4 * k], [(sx + 8) * k, -4 * k], [fx1 * k, G], [fx0 * k, G]], col)
  }
  for (const [rr, aa] of [[1, 0.10], [0.6, 0.12]] as const) {
    ctx.globalAlpha = aa * flash * flick; ctx.fillStyle = col
    ctx.beginPath(); ctx.ellipse(210 * k, G, 150 * k * rr, 9 * k * rr, 0, 0, Math.PI * 2); ctx.fill()
  }
  ctx.globalAlpha = 1
}
export const PRINCESS_LIGHT_COLOR_MOCKUP: MockupMove = {
  ...mockup('princess-light-color', '灯り E 照明の色（舞台を照らす色が、突くたびに次の技の色へ1段寄る）', B, body(),
    [...jabPhases(B, '1回目の突き・照明の色が次の技の色へ1段寄る', '2回目の突き・もう1段'),
      ...lungePhases(B, '合図：色が決まる（次の技の色）')], EB.step,
    '拍は前の版と同じ仮の値。帯の黄色＝色が寄った瞬間。色は仮に赤（どの先人の技を使うかで色を変える形を想定）。技の後にあたたかい白へ戻る。数値は技を組む時に決め直す。'),
  stage: { back: drawColor },
}

export const PRINCESS_LIGHT_MOCKUPS = [PRINCESS_LIGHT_FOOT_MOCKUP, PRINCESS_LIGHT_CHANDELIER_MOCKUP, PRINCESS_LIGHT_SPOT_MOCKUP, PRINCESS_LIGHT_TRAIL_MOCKUP, PRINCESS_LIGHT_COLOR_MOCKUP]
